import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { optimierungApi } from '../api';

// ─── Farben & Utilities ───────────────────────────────────────────────────────

const PRIORITY_COLORS = {
  sofort_pruefen:        { bg: '#3b0000', border: '#ef4444', badge: '#ef4444', text: '#fca5a5' },
  optimierung_empfohlen: { bg: '#1c1400', border: '#f59e0b', badge: '#f59e0b', text: '#fcd34d' },
  beobachten:            { bg: '#0c1a2e', border: '#3b82f6', badge: '#3b82f6', text: '#93c5fd' },
  unauffaellig:          { bg: '#0f172a', border: '#334155', badge: '#64748b', text: '#64748b' },
};

const SIGNAL_TYPE_COLORS = {
  FAKT:      { bg: '#14532d', color: '#86efac', label: 'FAKT' },
  INDIKATOR: { bg: '#1e1b4b', color: '#a5b4fc', label: 'INDIKATOR' },
  SCHÄTZUNG: { bg: '#431407', color: '#fed7aa', label: 'SCHÄTZUNG' },
};

function scoreColor(score) {
  if (score >= 75) return '#ef4444';
  if (score >= 50) return '#f59e0b';
  if (score >= 25) return '#3b82f6';
  return '#22c55e';
}

function ScoreRing({ score, size = 56 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const color = scoreColor(score);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1e293b" strokeWidth={6} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={`${fill} ${circ - fill}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        fill={color} fontSize={size < 48 ? 10 : 13} fontWeight={700}>{score}</text>
    </svg>
  );
}

function SignalBadge({ signal }) {
  const tc = SIGNAL_TYPE_COLORS[signal.type] || SIGNAL_TYPE_COLORS['INDIKATOR'];
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 3, flexShrink: 0,
        background: tc.bg, color: tc.color, marginTop: 1,
      }}>{tc.label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{signal.title}</div>
        {signal.description && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>{signal.description}</div>
        )}
      </div>
      <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0, marginTop: 1 }}>+{signal.weight}</span>
    </div>
  );
}

function PriorityBadge({ priority, text }) {
  const c = PRIORITY_COLORS[priority] || PRIORITY_COLORS.unauffaellig;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>{text}</span>
  );
}

function ContractCard({ rec, onClick }) {
  const c = PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.unauffaellig;
  return (
    <div onClick={onClick} style={{
      background: '#1e293b', border: `1px solid ${c.border}`, borderRadius: 10,
      padding: 16, cursor: 'pointer', transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <ScoreRing score={rec.score} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {rec.contractTitle}
            </span>
            <PriorityBadge priority={rec.priority} text={rec.priorityText} />
          </div>
          {rec.provider && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{rec.provider}</div>
          )}
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {rec.monthlyCost > 0 && (
              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                {rec.monthlyCost.toFixed(2)} €/Monat
              </span>
            )}
            {rec.deadlineDays !== null && rec.deadlineDays !== undefined && rec.deadlineDays <= 90 && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 6,
                background: rec.deadlineDays <= 14 ? '#3b0000' : '#1c1400',
                color: rec.deadlineDays <= 14 ? '#fca5a5' : '#fcd34d',
              }}>
                {rec.deadlineDays < 0 ? 'Frist verpasst' : `Frist in ${rec.deadlineDays}d`}
              </span>
            )}
            {rec.autoRenewal && (
              <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, background: '#1e1b4b', color: '#a5b4fc' }}>
                Auto-Verlängerung
              </span>
            )}
          </div>
        </div>
      </div>
      {rec.signals.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {rec.signals.slice(0, 3).map((s, i) => (
            <span key={i} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 12, background: '#0f172a', color: '#94a3b8' }}>
              {s.title}
            </span>
          ))}
          {rec.signals.length > 3 && (
            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 12, background: '#0f172a', color: '#475569' }}>
              +{rec.signals.length - 3} weitere
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DetailDrawer({ rec, onClose, navigate }) {
  if (!rec) return null;
  const c = PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.unauffaellig;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, display: 'flex',
    }} onClick={onClose}>
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.6)' }} />
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 500, background: '#0f172a',
        borderLeft: `2px solid ${c.border}`, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        padding: 0,
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScoreRing score={rec.score} size={56} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{rec.contractTitle}</div>
            {rec.provider && <div style={{ fontSize: 12, color: '#64748b' }}>{rec.provider}</div>}
            <div style={{ marginTop: 4 }}>
              <PriorityBadge priority={rec.priority} text={rec.priorityText} />
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost">✕</button>
        </div>

        <div style={{ padding: '16px 20px', flex: 1 }}>
          {/* Kostenfakten */}
          {rec.monthlyCost > 0 && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: '#1e293b', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Monatlich</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>{rec.monthlyCost.toFixed(2)} €</div>
              </div>
              <div style={{ flex: 1, background: '#1e293b', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Jährlich</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>{rec.annualCost.toFixed(2)} €</div>
              </div>
            </div>
          )}

          {/* Signals */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Erkannte Signale ({rec.signals.length})
            </div>
            {rec.signals.length === 0 ? (
              <div style={{ fontSize: 13, color: '#64748b', padding: 12, background: '#1e293b', borderRadius: 8 }}>
                Keine Auffälligkeiten erkannt.
              </div>
            ) : (
              rec.signals.map((s, i) => <SignalBadge key={i} signal={s} />)
            )}
          </div>

          {/* Einsparpotenzial */}
          {rec.potentialSaving && (
            <div style={{ marginBottom: 16, background: '#1e293b', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Einsparpotenzial
              </div>
              <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.6 }}>
                {rec.potentialSaving.label}
              </div>
            </div>
          )}

          {/* Aktionen */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Empfohlene Aktionen
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rec.recommendedActions.map((a, i) => (
                <button key={i} onClick={() => { navigate(a.url); onClose(); }} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
                  <span style={{ fontSize: 18 }}>{a.icon}</span>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hauptseite ───────────────────────────────────────────────────────────────

export default function OptimierungPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [sortBy, setSortBy] = useState('score');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await optimierungApi.analyse();
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Analyse fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: '#6366f1', fontSize: 24 }}>⏳ Analysiere Vertragsbestand...</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: '#3b0000', border: '1px solid #ef4444', borderRadius: 10, padding: 20, color: '#fca5a5' }}>
        {error}
      </div>
    </div>
  );

  const { summary, recommendations } = data || {};
  const totalW = Object.values(summary?.scoreWeights || {}).reduce((a, b) => a + b, 0);

  // Filtern
  let displayed = recommendations || [];
  if (filter === 'sofort') displayed = displayed.filter(r => r.priority === 'sofort_pruefen');
  else if (filter === 'pruefen') displayed = displayed.filter(r => r.priority === 'optimierung_empfohlen');
  else if (filter === 'beobachten') displayed = displayed.filter(r => r.priority === 'beobachten');

  // Sortieren
  if (sortBy === 'score') displayed = [...displayed].sort((a, b) => b.score - a.score);
  else if (sortBy === 'kosten') displayed = [...displayed].sort((a, b) => (b.monthlyCost || 0) - (a.monthlyCost || 0));
  else if (sortBy === 'frist') displayed = [...displayed].sort((a, b) => {
    const da = a.deadlineDays ?? 9999;
    const db = b.deadlineDays ?? 9999;
    return da - db;
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px 100px' }}>
      {/* Seitenheader */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
          💡 Vertragsoptimierung
        </h1>
        <p style={{ color: '#64748b', marginTop: 4, fontSize: 13 }}>
          Nachvollziehbare Analyse Ihres Vertragsbestands – ohne erfundene Marktpreise.
        </p>
      </div>

      {/* KPI-Karten */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          <KpiCard label="Analysiert" value={summary.contractsAnalyzed} icon="📋" />
          <KpiCard label="Handlungsbedarf" value={summary.highPriority} icon="🔴"
            color={summary.highPriority > 0 ? '#ef4444' : '#22c55e'} />
          <KpiCard label="Prüfen" value={summary.reviewPriority} icon="🟡"
            color={summary.reviewPriority > 0 ? '#f59e0b' : '#22c55e'} />
          <KpiCard label="Empfehlungen" value={summary.recommendations} icon="💡" />
          {summary.potentialAvoidableAnnualCost > 0 && (
            <KpiCard
              label="Max. vermeidbare Kosten/Jahr"
              value={`${summary.potentialAvoidableAnnualCost.toFixed(0)} €`}
              icon="💰"
              hint="FAKT: vollständige Kündigung aller rot markierten Verträge"
              color="#f59e0b"
            />
          )}
        </div>
      )}

      {/* Gewichtungslegende */}
      <details style={{ marginBottom: 20 }}>
        <summary style={{ color: '#64748b', fontSize: 12, cursor: 'pointer', userSelect: 'none', padding: '8px 0' }}>
          Wie wird der Score berechnet? ▾
        </summary>
        <div style={{ background: '#1e293b', borderRadius: 8, padding: 14, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
            Score-Gewichte (gesamt: {totalW} Punkte, normiert auf 0–100):
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 }}>
            {Object.entries(summary?.scoreWeights || {}).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#94a3b8' }}>{k.replace(/_/g, ' ')}</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{v} Pkt</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: '#475569' }}>
            Signaltypen: FAKT = direkt aus Vertragsdaten · INDIKATOR = abgeleitete Auffälligkeit · SCHÄTZUNG = transparent berechnet
          </div>
        </div>
      </details>

      {/* Filter + Sortierung */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {[
          { key: 'alle', label: 'Alle' },
          { key: 'sofort', label: '🔴 Sofort prüfen' },
          { key: 'pruefen', label: '🟡 Optimierung' },
          { key: 'beobachten', label: '🔵 Beobachten' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: 'none',
            background: filter === f.key ? '#818cf8' : '#1e293b',
            color: filter === f.key ? '#fff' : '#94a3b8',
            fontWeight: filter === f.key ? 700 : 400,
          }}>{f.label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Sortierung:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
            borderRadius: 6, padding: '4px 8px', fontSize: 12,
          }}>
            <option value="score">Score</option>
            <option value="kosten">Monatliche Kosten</option>
            <option value="frist">Fristdringlichkeit</option>
          </select>
        </div>
      </div>

      {/* Empfehlungsliste */}
      {displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
          {recommendations?.length === 0
            ? 'Keine auffälligen Verträge gefunden. Gut aufgestellt!'
            : 'Kein Vertrag in dieser Kategorie.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {displayed.map(rec => (
            <ContractCard key={rec.contractId} rec={rec} onClick={() => setSelected(rec)} />
          ))}
        </div>
      )}

      {/* Detail-Drawer */}
      {selected && (
        <DetailDrawer rec={selected} onClose={() => setSelected(null)} navigate={navigate} />
      )}

      {/* Refresh */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <button onClick={load} className="btn-secondary">
          🔄 Analyse aktualisieren
        </button>
        {data?.generatedAt && (
          <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
            Generiert: {new Date(data.generatedAt).toLocaleTimeString('de-DE')}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, color, hint }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#f1f5f9', marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}
